import React from 'react'
import { Switch, Route, Redirect } from 'react-router-dom'
import { Header, Loader, PrivateRoute } from 'components'
import { RedirectHandler } from 'app/RedirectHandler'
import { SessionHandler } from 'app/SessionHandler'

//404
const FourOFour = React.lazy(() => import('app/FourOFour'))

//SUPPLIER PAGES
const SalesHome = React.lazy(() => import('pages/supplier/SalesHome'))
const StoreStock = React.lazy(() => import('pages/supplier/StoreStock'))
const DepotDelivery = React.lazy(() => import('pages/supplier/DepotDelivery'))
const ProductReference = React.lazy(() => import('pages/supplier/ProductReference'))
const DistributionOutbound = React.lazy(() => import('pages/supplier/DistributionOutbound'))
const PurchaseOrder = React.lazy(() => import('pages/supplier/PurchaseOrder'))
const PromotionForecast = React.lazy(() => import('pages/supplier/PromotionForecast'))
const ViewReports = React.lazy(() => import('pages/supplier/ViewReports'))
const SupplierService = React.lazy(() => import('pages/supplier/SupplierService'))
const DepotStock = React.lazy(() => import('pages/supplier/DepotStock'))
const SalesForecast = React.lazy(() => import('pages/supplier/salesOrderForecast/SalesForecast'))
const OrderForecast = React.lazy(() => import('pages/supplier/salesOrderForecast/OrderForecast'))
const WastageAndMarkdown = React.lazy(() => import('pages/supplier/wastageMarkdownAvailability/WastageAndMarkdown'))
const Availability = React.lazy(() => import('pages/supplier/wastageMarkdownAvailability/Availability'))
const RangedProducts = React.lazy(() => import('pages/supplier/RangedProducts'))
const DepotRejections = React.lazy(() => import('pages/supplier/DepotRejections'))

//ADMIN PAGES
const AddMessage = React.lazy(() => import('pages/admin/supplierMessaging/AddMessage'))
const AmendMessage = React.lazy(() => import('pages/admin/supplierMessaging/AmendMessage'))
const GrantAccess = React.lazy(() => import('pages/admin/manageAccess/GrantAccess'))
const ModifyAccess = React.lazy(() => import('pages/admin/manageAccess/ModifyAccess'))
const CreateUserRoles = React.lazy(() => import('pages/admin/userRoles/CreateUserRoles'))
const AmendUserRoles = React.lazy(() => import('pages/admin/userRoles/AmendUserRoles'))
const CreateCompanyProfile = React.lazy(() => import('pages/admin/manageCompanyProfile/CreateCompanyProfile'))
const ManageCompanyProfile = React.lazy(() => import('pages/admin/manageCompanyProfile/ManageCompanyProfile'))
const ViewAssociatedGroups = React.lazy(() => import('pages/admin/manageCompanyProfile/ViewAssociatedGroups'))

// SUPPLIER/CONSOLIDATOR/HAULIER ADMIN PAGES
const RemoveUser = React.lazy(() => import('pages/admin/manageAccess/RemoveUser'))
const AddConsolidatorHaulier = React.lazy(() => import('pages/admin/manageCompanyProfile/AddConsolidatorHaulier'))

//SHARED PAGES
const Home = React.lazy(() => import('pages/shared/Home'))
const TermsAndConditions = React.lazy(() => import('pages/shared/TermsAndConditions'))
const Sitemap = React.lazy(() => import('pages/shared/Sitemap'))
const MorrisonsDocuments = React.lazy(() => import('pages/shared/MorrisonsDocuments'))

export const MainApp: React.FC = () => {
  return (
    <div data-testid="main-app">
      <SessionHandler />
      <RedirectHandler />
      <Header isLoggedIn />

      <Switch>
        {/* HOME ROUTE */}
        <PrivateRoute
          path="/home"
          requiredAccessLevel={[
            'Supplier Reports',
            'Manage Access',
            'Manage Communications',
            'Supplier Admin',
            'Consolidator/Haulier Admin',
          ]}
        >
          <React.Suspense fallback={<Loader />}>
            <Home />
          </React.Suspense>
        </PrivateRoute>

        {/* SUPPLIER ROUTES */}
        <PrivateRoute
          path="/supplier/sales-home"
          requiredAccessLevel={['Sales', 'Online Sales', 'Store Stock', 'Wholesale Sales']}
        >
          <React.Suspense fallback={<Loader />}>
            <SalesHome />
          </React.Suspense>
        </PrivateRoute>
        <PrivateRoute path="/supplier/store-stock" exact requiredAccessLevel="Store Stock">
          <React.Suspense fallback={<Loader />}>
            <StoreStock />
          </React.Suspense>
        </PrivateRoute>
        <PrivateRoute path="/supplier/depot-delivery" exact requiredAccessLevel="Depot Delivery">
          <React.Suspense fallback={<Loader />}>
            <DepotDelivery />
          </React.Suspense>
        </PrivateRoute>
        <PrivateRoute path="/supplier/depot-stock" exact requiredAccessLevel="Depot Stock">
          <React.Suspense fallback={<Loader />}>
            <DepotStock />
          </React.Suspense>
        </PrivateRoute>
        <PrivateRoute
          path="/supplier/distribution-centre-outbound"
          exact
          requiredAccessLevel="Distribution Centre Outbound"
        >
          <React.Suspense fallback={<Loader />}>
            <DistributionOutbound />
          </React.Suspense>
        </PrivateRoute>
        <PrivateRoute path="/supplier/supplier-service" requiredAccessLevel="Supplier Service">
          <React.Suspense fallback={<Loader />}>
            <SupplierService />
          </React.Suspense>
        </PrivateRoute>
        <PrivateRoute path="/supplier/purchase-order" requiredAccessLevel="Purchase Order">
          <React.Suspense fallback={<Loader />}>
            <PurchaseOrder />
          </React.Suspense>
        </PrivateRoute>
        <PrivateRoute path="/supplier/promotion-forecast" exact requiredAccessLevel="Promotion Forecast">
          <React.Suspense fallback={<Loader />}>
            <PromotionForecast />
          </React.Suspense>
        </PrivateRoute>
        <PrivateRoute path="/supplier/product-reference" requiredAccessLevel="Product Reference">
          <React.Suspense fallback={<Loader />}>
            <ProductReference />
          </React.Suspense>
        </PrivateRoute>
        <PrivateRoute path="/supplier/sales-forecast" exact requiredAccessLevel="Sales Forecast">
          <React.Suspense fallback={<Loader />}>
            <SalesForecast />
          </React.Suspense>
        </PrivateRoute>
        <PrivateRoute path="/supplier/order-forecast" exact requiredAccessLevel="Supplier Order Forecast">
          <React.Suspense fallback={<Loader />}>
            <OrderForecast />
          </React.Suspense>
        </PrivateRoute>
        <PrivateRoute path="/supplier/wastage-and-markdown" exact requiredAccessLevel="Wastage And Markdown">
          <React.Suspense fallback={<Loader />}>
            <WastageAndMarkdown />
          </React.Suspense>
        </PrivateRoute>
        <PrivateRoute path="/supplier/availability" exact requiredAccessLevel="Availability">
          <React.Suspense fallback={<Loader />}>
            <Availability />
          </React.Suspense>
        </PrivateRoute>
        <PrivateRoute path="/supplier/ranged-products" requiredAccessLevel="Ranged Products">
          <React.Suspense fallback={<Loader />}>
            <RangedProducts />
          </React.Suspense>
        </PrivateRoute>
        <PrivateRoute path="/supplier/depot-rejections" requiredAccessLevel="Depot Rejections">
          <React.Suspense fallback={<Loader />}>
            <DepotRejections />
          </React.Suspense>
        </PrivateRoute>
        <PrivateRoute path="/supplier/morrisons-documents" exact requiredAccessLevel="Morrisons Documents (Supplier)">
          <React.Suspense fallback={<Loader />}>
            <MorrisonsDocuments />
          </React.Suspense>
        </PrivateRoute>
        <PrivateRoute path="/supplier/reports" exact requiredAccessLevel="Supplier Reports">
          <React.Suspense fallback={<Loader />}>
            <ViewReports />
          </React.Suspense>
        </PrivateRoute>

        {/* ADMIN ROUTES */}
        <PrivateRoute path="/admin/grant-user-access" requiredAccessLevel="Grant Access">
          <React.Suspense fallback={<Loader />}>
            <GrantAccess />
          </React.Suspense>
        </PrivateRoute>
        <PrivateRoute path="/admin/modify-user-access" exact requiredAccessLevel="Modify Access">
          <React.Suspense fallback={<Loader />}>
            <ModifyAccess />
          </React.Suspense>
        </PrivateRoute>
        <PrivateRoute path="/admin/create-user-roles" exact requiredAccessLevel="Create User Roles">
          <React.Suspense fallback={<Loader />}>
            <CreateUserRoles />
          </React.Suspense>
        </PrivateRoute>
        <PrivateRoute path="/admin/amend-user-roles" exact requiredAccessLevel="Amend User Roles">
          <React.Suspense fallback={<Loader />}>
            <AmendUserRoles />
          </React.Suspense>
        </PrivateRoute>
        <PrivateRoute path="/admin/create-company-profile" exact requiredAccessLevel="Add Company/Group">
          <React.Suspense fallback={<Loader />}>
            <CreateCompanyProfile />
          </React.Suspense>
        </PrivateRoute>
        <PrivateRoute path="/admin/manage-company-profile" exact requiredAccessLevel="Manage Company/Group Profile">
          <React.Suspense fallback={<Loader />}>
            <ManageCompanyProfile />
          </React.Suspense>
        </PrivateRoute>
        <PrivateRoute path="/admin/view-associated-groups" exact requiredAccessLevel="View Associated Groups">
          <React.Suspense fallback={<Loader />}>
            <ViewAssociatedGroups />
          </React.Suspense>
        </PrivateRoute>
        <PrivateRoute path="/admin/add-message" exact requiredAccessLevel="Add New Message">
          <React.Suspense fallback={<Loader />}>
            <AddMessage />
          </React.Suspense>
        </PrivateRoute>
        <PrivateRoute path="/admin/amend-message" exact requiredAccessLevel="Amend/Delete Message">
          <React.Suspense fallback={<Loader />}>
            <AmendMessage />
          </React.Suspense>
        </PrivateRoute>
        <PrivateRoute path="/admin/morrisons-documents" requiredAccessLevel="Morrisons Documents (Admin)">
          <React.Suspense fallback={<Loader />}>
            <MorrisonsDocuments />
          </React.Suspense>
        </PrivateRoute>

        {/* SUPPLIER ADMIN ROUTES */}
        <PrivateRoute path="/supplier-admin/remove-user" exact requiredAccessLevel="Supplier Admin">
          <React.Suspense fallback={<Loader />}>
            <RemoveUser backButtonText={`Supplier Admin`} backButtonRoute={`/home/supplier-admin`} />
          </React.Suspense>
        </PrivateRoute>
        <PrivateRoute path="/supplier-admin/add-consolidator-haulier" exact requiredAccessLevel="Supplier Admin">
          <React.Suspense fallback={<Loader />}>
            <AddConsolidatorHaulier />
          </React.Suspense>
        </PrivateRoute>

        {/* CONSOLIDATOR/HAULIER ADMIN ROUTES */}
        <PrivateRoute
          path="/consolidator-haulier-admin/remove-user"
          exact
          requiredAccessLevel="Consolidator/Haulier Admin"
        >
          <React.Suspense fallback={<Loader />}>
            <RemoveUser
              backButtonText={`Consolidator/Haulier Admin`}
              backButtonRoute={`/home/consolidator-haulier-admin`}
            />
          </React.Suspense>
        </PrivateRoute>

        {/* MISC ROUTES */}
        <Route path="/tnc" exact>
          <React.Suspense fallback={<Loader />}>
            <TermsAndConditions />
          </React.Suspense>
        </Route>
        <PrivateRoute path="/sitemap" exact>
          <React.Suspense fallback={<Loader />}>
            <Sitemap />
          </React.Suspense>
        </PrivateRoute>

        {/* 404 ROUTE */}
        <Route path="/">
          <Redirect from="/" to="/home" />
          <React.Suspense fallback={<Loader />}>
            <FourOFour errorCode="404" />
          </React.Suspense>
        </Route>
      </Switch>
    </div>
  )
}
